import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { QuestionBreakout } from './AIAssistant';
import { teacherApi } from '../data/api';
import { questionKey } from '../data/homeHelpEvidence';

export default function HomeQuestionViewer({ auth, classId, questions, text, onClose }) {
  const [state, setState] = useState(() => ({ text, view: 'grid', details: {}, items: questions.map(q => ({
    key: questionKey(q), record: q.record, student: q.student,
    label: q.name ? `${q.name}’s answer` : q.label || 'A question the class missed',
  })) }));
  const root = useRef(null);
  useEffect(() => { root.current?.querySelector('button')?.focus(); }, [state.view]);
  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    const items = questions.map(q => ({ ...q, key: questionKey(q) }));
    const update = (item, detail) => { if (active) setState(s => ({ ...s, details: { ...s.details, [item.key]: detail } })); };
    const timer = setTimeout(() => controller.abort(), 25000);
    Promise.allSettled(items.map(async item => {
      try {
        const q = await teacherApi(auth, 'getQuestionDetail', { classID: classId, recordID: item.record, userId: item.student }, { signal: controller.signal });
        update(item, q?.questionText ? { q: q.student && item.name ? { ...q, student: { ...q.student, name: item.name } } : q } : { error: true });
      } catch { update(item, { error: true }); }
    })).finally(() => clearTimeout(timer));
    return () => { active = false; clearTimeout(timer); controller.abort(); };
  }, [auth, classId, questions]);
  useEffect(() => {
    const previous = document.activeElement;
    const focusable = () => [...(root.current?.querySelectorAll('button:not(:disabled), a[href], input:not(:disabled), [tabindex="0"]') || [])].filter(el => el.getClientRects().length);
    focusable()[0]?.focus();
    const keydown = e => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
      if (e.key !== 'Tab') return;
      const nodes = focusable(); const first = nodes[0]; const last = nodes[nodes.length - 1];
      if (!root.current?.contains(document.activeElement)) { e.preventDefault(); first?.focus(); }
      else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last?.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus(); }
    };
    document.addEventListener('keydown', keydown);
    return () => { document.removeEventListener('keydown', keydown); if (previous?.isConnected) previous.focus(); };
  }, [onClose]);
  return createPortal(<div ref={root}><QuestionBreakout state={state} classId={classId} chatUser={auth?.user?.sub || auth?.user?.username || classId}
    onView={view => setState(s => ({ ...s, view }))} onClose={onClose} /></div>, document.body);
}
